import { ApiRoutes } from "@/enums/apiRoutes.enum";
import axios from "@/lib/axios";
import {
  CouponDemographics,
  CouponPeak,
  Delta,
  HomeSummary,
  LiveTransaction,
  MauMonth,
  MostVisitedStore,
  RankShare,
  ReceiptsByHour,
  StoreCompare,
  StoreMetric,
  StorePerTx,
  StoreStats,
  TopCouponStat,
  WindowKey,
} from "@/types/stats";

const withQuery = (
  route: string,
  params: Record<string, string | number | boolean | undefined>,
) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `${route}?${qs}` : route;
};

/**
 * Stats API (v2). All calls go same-origin through the BFF proxy
 * (src/pages/api/v2/[...slug].ts) which injects the bearer token.
 */
export const statsService = {
  /**
   * All Home headline cards in one call. The 5 windowed cards follow `window`
   * (window vs prior window); today + registered total stay window-independent.
   */
  async getHomeSummary(window: WindowKey): Promise<HomeSummary> {
    const response = await axios.get<HomeSummary>(
      withQuery(ApiRoutes.STATS_HOME_SUMMARY, { window }),
    );
    return response.data;
  },

  /** Distinct app users per month, last 12 months (current month partial). */
  async getMauMonthly(): Promise<MauMonth[]> {
    const response = await axios.get<MauMonth[]>(ApiRoutes.STATS_MAU_MONTHLY);
    return response.data;
  },

  /** Last N receipts (default 5, max 50). LIVE. */
  async getLiveTransactions(limit = 5): Promise<LiveTransaction[]> {
    const response = await axios.get<LiveTransaction[]>(
      withQuery(ApiRoutes.STATS_TRANSACTIONS_LIVE, { limit }),
    );
    return response.data;
  },

  /** Per-store transaction count + revenue. LIVE. Returns [] on upstream error. */
  async getTransactionsPerStore(window: WindowKey): Promise<StorePerTx[]> {
    const response = await axios.get<StorePerTx[]>(
      withQuery(ApiRoutes.STATS_TRANSACTIONS_PER_STORE, { window }),
    );
    return response.data;
  },

  /** Average receipt value, window vs prior window. LIVE. */
  async getAvgTransaction(window: WindowKey, store?: string): Promise<Delta> {
    const response = await axios.get<Delta>(
      withQuery(ApiRoutes.STATS_AVG_TRANSACTION, { window, store }),
    );
    return response.data;
  },

  /** Ranked stores by receipts (or unique visitors). LIVE. */
  async getMostVisitedStores(
    window: WindowKey,
    unique = false,
  ): Promise<MostVisitedStore[]> {
    const response = await axios.get<MostVisitedStore[]>(
      withQuery(ApiRoutes.STATS_MOST_VISITED_STORES, { window, unique }),
    );
    return response.data;
  },

  /** Bronze/silver/gold split of all customers. LIVE snapshot. */
  async getRankDistribution(): Promise<RankShare[]> {
    const response = await axios.get<RankShare[]>(
      ApiRoutes.STATS_RANK_DISTRIBUTION,
    );
    return response.data;
  },

  /** 7×24 dow×hour heatmap. ROLLUP — carries meta.asOf. */
  async getReceiptsByHour(
    window: WindowKey,
    store = "ALL",
  ): Promise<ReceiptsByHour> {
    const response = await axios.get<ReceiptsByHour>(
      withQuery(ApiRoutes.STATS_RECEIPTS_BY_HOUR, { window, store }),
    );
    return response.data;
  },

  /** Top coupons by redemptions. LIVE. */
  async getTopCoupons(
    window: WindowKey,
    limit = 5,
  ): Promise<TopCouponStat[]> {
    const response = await axios.get<TopCouponStat[]>(
      withQuery(ApiRoutes.STATS_COUPONS_TOP, { window, limit }),
    );
    return response.data;
  },

  /**
   * Everything about one store in one call — spend/visitor Deltas are LIVE,
   * demographics/peak are ROLLUP. {id} = ime_ducana.
   */
  async getStoreStats(id: string, window: WindowKey): Promise<StoreStats> {
    const response = await axios.get<StoreStats>(
      withQuery(`${ApiRoutes.STORE_STATS}/${encodeURIComponent(id)}/stats`, {
        window,
      }),
    );
    return response.data;
  },

  /** 1-vs-1 or 1-vs-all (b="all") comparison for one metric. LIVE. */
  async compareStores(
    a: string,
    b: string,
    metric: StoreMetric,
    window: WindowKey,
  ): Promise<StoreCompare> {
    const response = await axios.get<StoreCompare>(
      withQuery(`${ApiRoutes.STORE_STATS}/compare`, { a, b, metric, window }),
    );
    return response.data;
  },

  /** Who redeems one coupon — distinct-redeemer demographics. ROLLUP. */
  async getCouponDemographics(
    nftId: number,
    window: WindowKey,
    store = "ALL",
  ): Promise<CouponDemographics> {
    const response = await axios.get<CouponDemographics>(
      withQuery(`${ApiRoutes.STATS_COUPONS}/${nftId}/demographics`, {
        window,
        store,
      }),
    );
    return response.data;
  },

  /** Redemption timing for one coupon (hour-of-day by default). LIVE. */
  async getCouponPeak(
    nftId: number,
    window: WindowKey,
    store = "ALL",
    by: "hour" | "dow" = "hour",
  ): Promise<CouponPeak> {
    const response = await axios.get<CouponPeak>(
      withQuery(`${ApiRoutes.STATS_COUPONS}/${nftId}/peak`, {
        window,
        store,
        by,
      }),
    );
    return response.data;
  },
};
